import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HeartOutlined, ArrowRightOutlined, ThunderboltOutlined,
  RobotOutlined, CloudOutlined, CodeOutlined, TrophyOutlined,
  AppstoreOutlined, StarOutlined,
} from '@ant-design/icons';

/* =========================================================
   萤火秘境 · 首页
   全屏视频 Hero → 灵感溪流 → 森林入口 → 社区精选
   ========================================================= */

const STREAM_STATS = [
  { value: '12,800+', label: '创作者留下的灵感', color: '#F5E7A8' },
  { value: '4,200+', label: '已经完成的作品', color: '#69B89A' },
  { value: '68', label: '被世界看见的瞬间', color: '#A68BD9' },
];

const FOREST_ENTRIES = [
  {
    icon: <RobotOutlined />,
    title: '智能伙伴',
    desc: '你的第一个萤火虫，陪你探索创意方向',
    color: '#E8C85A',
    path: '/agents',
  },
  {
    icon: <CloudOutlined />,
    title: '作品落地',
    desc: '从想法到现实，只需要一次呼吸',
    color: '#69B89A',
    path: '/projects',
  },
  {
    icon: <CodeOutlined />,
    title: '自由构建',
    desc: '低代码可视化，释放每一份创造力',
    color: '#8EB9D6',
    path: '/projects',
  },
  {
    icon: <TrophyOutlined />,
    title: '被世界发现',
    desc: '直通顶级赛事，让你的作品发光',
    color: '#A68BD9',
    path: '/settings',
  },
];

const SHOWCASE = [
  { title: '北冥水世界海洋馆Logo', author: '墨白', likes: 1240, gradient: 'linear-gradient(180deg, #0A2A3A 0%, #0E3A5A 50%, #1A5A8A 100%)' },
  { title: '穿越山海文旅活动全案', author: '北风', likes: 980, gradient: 'linear-gradient(180deg, #1A0A3A 0%, #2A1A5A 50%, #4A2A8A 100%)' },
  { title: '幻境森林音乐节IP', author: '星尘', likes: 2100, gradient: 'linear-gradient(180deg, #0A2A1A 0%, #0E4A2A 50%, #1A7A4A 100%)' },
  { title: '智慧城市数据可视化', author: '流光', likes: 1560, gradient: 'linear-gradient(180deg, #2A1A0A 0%, #4A3A1A 50%, #7A5A2A 100%)' },
  { title: '非遗文创品牌全案', author: '青竹', likes: 870, gradient: 'linear-gradient(180deg, #2A0A1A 0%, #4A1A3A 50%, #7A2A5A 100%)' },
  { title: '儿童教育AI助手', author: '晨曦', likes: 1890, gradient: 'linear-gradient(180deg, #0A1A3A 0%, #1A2A5A 50%, #2A4A8A 100%)' },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div style={{ minHeight: '100vh', position: 'relative' }}>
      {/* ───── VIDEO HERO — 100vh ───── */}
      <section style={{
        position: 'relative',
        height: '100vh',
        minHeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}>
        {/* Video Background */}
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Crect fill='%23071410' width='1920' height='1080'/%3E%3Ccircle cx='200' cy='300' r='120' fill='%230B1825'/%3E%3Ccircle cx='1600' cy='700' r='180' fill='%230B1825'/%3E%3C/svg%3E"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        >
          {/* Replace src with actual forest video */}
          <source src="/forest-fireflies.mp4" type="video/mp4" />
        </video>

        {/* Layer 1: Dark forest blend */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(5,15,12,0.45) 0%, rgba(7,20,16,0.8) 100%)',
          zIndex: 1,
        }} />

        {/* Layer 2: Mist overlay (CSS animated) */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse at 30% 60%,rgba(105,184,154,0.04) 0%,transparent 60%), radial-gradient(ellipse at 70% 30%,rgba(232,200,90,0.03) 0%,transparent 50%)',
          zIndex: 1,
          animation: 'mist-drift 8s ease-in-out infinite alternate',
        }} />

        {/* Layer 3: Glass content */}
        <div style={{
          position: 'relative', zIndex: 5,
          textAlign: 'center',
          maxWidth: 780,
          padding: '0 32px',
          animation: 'fadeSlideUp 800ms var(--ease) both',
        }}>
          {/* Top badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 16px', borderRadius: 24,
            background: 'rgba(232,200,90,0.06)',
            border: '1px solid rgba(232,200,90,0.15)',
            fontSize: 13, color: '#E8C85A', fontWeight: 500,
            marginBottom: 28,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#E8C85A', display: 'inline-block', boxShadow: '0 0 8px rgba(232,200,90,0.5)' }} />
            萤火秘境 · 创造生态系统
          </div>

          {/* Main headline */}
          <h1 style={{
            fontSize: 'clamp(36px, 6vw, 72px)',
            fontWeight: 600,
            lineHeight: 1.12,
            margin: '0 0 24px',
            letterSpacing: '-0.045em',
            fontFamily: 'var(--font-sans)',
          }}>
            <span className="gradient-text">当夜幕降临，</span>
            <br/>
            <span style={{ color: '#F3F5EF' }}>每一个灵感都会发光</span>
          </h1>

          {/* Sub */}
          <p style={{
            fontSize: 'clamp(16px, 2vw, 22px)',
            color: '#A9B8B0',
            maxWidth: 520, margin: '0 auto 40px',
            lineHeight: 1.75,
            fontWeight: 400,
          }}>
            AI 陪伴创作，让想象穿过黑夜，成为真实作品
          </p>

          {/* CTA */}
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn-firefly" style={{ height: 56, padding: '0 36px', fontSize: 15 }}
                    onClick={() => navigate('/projects')}>
              <ThunderboltOutlined /> 进入创作森林
            </button>
            <button className="btn-glass" style={{ height: 56, padding: '0 32px' }}
                    onClick={() => navigate('/community')}>
              <AppstoreOutlined /> 探索作品
            </button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div style={{
          position: 'absolute', bottom: 36, left: '50%', transform: 'translateX(-50%)',
          zIndex: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          animation: 'floatSoft 3s ease-in-out infinite',
        }}>
          <div style={{
            width: 20, height: 32, borderRadius: 12,
            border: '1.5px solid rgba(243,245,239,0.25)',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', top: 5, left: '50%', transform: 'translateX(-50%)',
              width: 3, height: 6, borderRadius: 2,
              background: 'rgba(243,245,239,0.5)',
              animation: 'fadeSlideUp 1.5s ease-in-out infinite',
            }} />
          </div>
          <span style={{ fontSize: 10, color: '#4A6070', letterSpacing: '0.08em' }}>SCROLL</span>
        </div>
      </section>

      {/* ───── 灵感溪流 ───── */}
      <section style={{
        maxWidth: 1200, margin: '0 auto', padding: '80px 40px 60px',
        textAlign: 'center',
      }}>
        <p style={{ fontSize: 13, color: '#64766D', marginBottom: 10, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          创作数据
        </p>
        <h2 style={{ fontSize: 'clamp(28px,4vw,40px)', fontWeight: 600, color: '#F3F5EF', margin: '0 0 48px', letterSpacing: '-0.03em' }}>
          灵感汇聚成溪
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 28 }}>
          {STREAM_STATS.map((s, i) => (
            <div key={i} className="glass-deep" style={{
              padding: '36px 24px',
              textAlign: 'center',
              transition: 'transform 250ms var(--ease)',
            }}>
              <div style={{
                fontSize: 36, fontWeight: 600,
                color: s.color,
                marginBottom: 8,
                lineHeight: 1.1,
              }}>{s.value}</div>
              <div style={{ fontSize: 14, color: '#64766D' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ───── 森林入口 ───── */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '60px 40px' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h2 style={{ fontSize: 'clamp(28px,4vw,40px)', fontWeight: 600, color: '#F3F5EF', margin: '0 0 10px', letterSpacing: '-0.03em' }}>
            森林中的四个入口
          </h2>
          <p style={{ color: '#64766D', fontSize: 15 }}>
            每一个方向，都通向不同的创造体验
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18 }}>
          {FOREST_ENTRIES.map((entry, i) => (
            <div key={i} className="glass-card" style={{
              padding: '28px 22px', cursor: 'pointer',
              textAlign: 'center',
              position: 'relative', overflow: 'hidden',
            }}
              onClick={() => navigate(entry.path)}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {/* glow */}
              <div style={{
                position: 'absolute', top: -20, right: -20,
                width: 80, height: 80, borderRadius: '50%',
                background: `${entry.color}08`,
                transform: hoveredIdx === i ? 'scale(2)' : 'scale(1)',
                transition: 'transform 400ms var(--ease)',
                pointerEvents: 'none',
              }} />

              <div style={{
                width: 48, height: 48, borderRadius: 14,
                background: `${entry.color}10`,
                border: `1px solid ${entry.color}25`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, color: entry.color,
                margin: '0 auto 16px',
                transition: 'all 250ms var(--ease)',
                transform: hoveredIdx === i ? 'scale(1.08)' : 'scale(1)',
              }}>
                {entry.icon}
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#F3F5EF', marginBottom: 8 }}>
                {entry.title}
              </div>
              <div style={{ fontSize: 13, color: '#64766D', lineHeight: 1.6 }}>
                {entry.desc}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ───── 社区精选 (置底) ───── */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '60px 40px 80px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 600, color: '#F3F5EF', margin: '0 0 4px', letterSpacing: '-0.02em' }}>
              社区精选
            </h2>
            <p style={{ color: '#64766D', fontSize: 13, margin: 0 }}>来自开发者的真实作品</p>
          </div>
          <button className="btn-ghost" style={{ height: 36, fontSize: 13 }}
                  onClick={() => navigate('/community')}>
            查看更多 <ArrowRightOutlined style={{ fontSize: 10 }} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
          {SHOWCASE.map((item, i) => (
            <div key={i} className="glass-card" style={{ overflow: 'hidden', cursor: 'pointer' }}>
              <div style={{
                height: 160, background: item.gradient,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative', overflow: 'hidden',
              }}>
                <StarOutlined style={{ fontSize: 36, color: 'rgba(255,255,255,0.12)' }} />
                {/* firefly */}
                {i % 2 === 0 && <div style={{
                  position: 'absolute', top: '22%', left: '14%',
                  width: 3, height: 3, borderRadius: '50%',
                  background: 'rgba(245,231,168,0.8)',
                  boxShadow: '0 0 8px rgba(245,231,168,0.5)',
                }} />}
                {i % 3 === 0 && <div style={{
                  position: 'absolute', top: '55%', right: '22%',
                  width: 2.5, height: 2.5, borderRadius: '50%',
                  background: 'rgba(232,200,90,0.6)',
                  boxShadow: '0 0 6px rgba(232,200,90,0.4)',
                }} />}
              </div>
              <div style={{ padding: '14px 16px' }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#F3F5EF', marginBottom: 8 }}>
                  {item.title}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: '#8BA8B8' }}>@{item.author}</span>
                  <span style={{ fontSize: 12, color: '#64766D' }}>
                    <HeartOutlined style={{ marginRight: 4 }} />{item.likes}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ───── Bottom CTA ───── */}
      <section style={{ padding: '80px 40px 100px', textAlign: 'center' }}>
        <div className="glass-deep" style={{
          maxWidth: 720, margin: '0 auto',
          padding: '56px 40px',
          background: 'linear-gradient(135deg, rgba(232,200,90,0.04) 0%, rgba(105,184,154,0.03) 50%, rgba(166,139,217,0.04) 100%)',
          border: '1px solid rgba(232,200,90,0.1)',
          position: 'relative', overflow: 'hidden',
        }}>
          {/* firefly dots */}
          <div style={{
            position: 'absolute', top: 18, left: 28,
            width: 4, height: 4, borderRadius: '50%',
            background: 'rgba(245,231,168,0.5)',
            boxShadow: '0 0 12px rgba(245,231,168,0.35)',
          }} />
          <div style={{
            position: 'absolute', bottom: 22, right: 36,
            width: 3, height: 3, borderRadius: '50%',
            background: 'rgba(232,200,90,0.5)',
            boxShadow: '0 0 10px rgba(232,200,90,0.3)',
          }} />
          <h2 style={{ fontSize: 'clamp(24px,3vw,32px)', fontWeight: 600, color: '#F3F5EF', marginBottom: 14, letterSpacing: '-0.02em' }}>
            准备好让你的想法上线了吗？
          </h2>
          <p style={{ color: '#64766D', fontSize: 15, margin: '0 0 28px' }}>
            从创意到部署 · 全程几分钟完成
          </p>
          <button className="btn-firefly" style={{ height: 52, padding: '0 40px', fontSize: 15 }}
                  onClick={() => navigate('/projects')}>
            <ThunderboltOutlined /> 立即开始
          </button>
        </div>
      </section>
    </div>
  );
}
